import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { setLenis, scrollToTop } from "@/lib/scroll";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import AuthPage from "@/pages/AuthPage";
import ResetPage from "@/pages/ResetPage";
import AccountPage from "@/pages/AccountPage";
import AdminPage from "@/pages/AdminPage";
import RouteSheetPage from "@/pages/RouteSheetPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AuthCallback from "@/pages/AuthCallback";
import AgreementPage from "@/pages/AgreementPage";
import ReviewsPage from "@/pages/ReviewsPage";

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    scrollToTop();
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Detect the Google OAuth return synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/catalog" element={<CatalogPage />} />
      <Route path="/listing/:id" element={<ListingDetailPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/reset-password" element={<ResetPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin/route" element={<RouteSheetPage />} />
      <Route path="/checkout/success" element={<PaymentSuccessPage />} />
      <Route path="/agreement" element={<AgreementPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      {/* Legacy Tote Rental Suite paths — forwarded old links land on the right new page */}
      <Route path="/packages" element={<Navigate to="/catalog" replace />} />
      <Route path="/start-booking" element={<Navigate to="/catalog" replace />} />
      <Route path="/tote-benefits" element={<Navigate to="/" replace />} />
      <Route path="/rental-flow" element={<Navigate to="/" replace />} />
      <Route path="/local-service" element={<Navigate to="/" replace />} />
      <Route path="/delivery-zone" element={<Navigate to="/" replace />} />
      <Route path="/questions" element={<Navigate to="/" replace />} />
      <Route path="/rental-terms" element={<Navigate to="/agreement" replace />} />
      <Route path="*" element={<HomePage />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    setLenis(lenis);
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollManager />
        <ChatWidget />
        <div className="min-h-screen bg-paper text-ink font-sans">
          <Navbar />
          <AppRouter />
          <Footer />
        </div>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
